/**
 * Bailey thinking out loud: the ideas the desk pet offers in a thought
 * bubble, and the rules for when. No React and no Three.js, so it's easy to
 * test; the pet gathers the facts (useThoughts.js) and draws the bubble.
 *
 * The one rule above the others: Bailey only thinks out loud when he has
 * something specific and true to say. Every number in a thought comes from
 * the salon's own data; with nothing worth saying he gets on with his antics.
 *
 * A thought is { id, key, said, label, cta, act, icon, score }:
 *   said   Bailey's words in the bubble
 *   cta    the bubble's action, a couple of words
 *   label  the person's words, for the ask box's idea rows
 *   act    { ask } sends it to Bailey as theirs, { go, params } or { path }
 *          opens a place
 *   key    what it's about ("2026-09-30:5"), so a thought acted on comes
 *          back when its facts change
 */
import moment from 'moment'

export const RHYTHM = {
  firstAfter: 20 * 1000, // quiet time on a page before the first thought
  gap: 4 * 60 * 1000, // between sessions, and only after a change of page
  samePageGap: 12 * 60 * 1000, // or this long on the same page
  perHour: 5,
  perSession: 3,
  shownPerDay: 2, // an idea shown this often today without a tap waits for tomorrow
  snoozeDays: 3, // closing an idea hides it this long
  doneDays: 7, // an idea acted on stays away this long, unless its facts change
  quietAfter: 3, // closes in a row before Bailey keeps his ideas to himself for the day
}

export const QUIET_LINE = 'I’ll keep my ideas to myself today.'

const DAY = 24 * 60 * 60 * 1000
const words = (s) => String(s || '').trim().split(/\s+/).filter(Boolean).length

/** How long a thought stays up: long enough to read, never a wait. */
export const readMs = (text) => Math.min(9000, Math.max(5000, 1600 + words(text) * 300))

// "Tomorrow" -> "tomorrow’s", "Wednesday" -> "Wednesday’s"
const possessive = (day) => `${/^(today|tomorrow)$/i.test(day) ? day.toLowerCase() : day}’s`
const plural = (n, one, many) => (n === 1 ? one : many)

/**
 * What Bailey could think about here and now, best first. `can` says which
 * places this person may use (social, brandKit, finance); `brand` whether
 * the brand kit is saved and has a logo; `drafts` and `planned` count Social
 * posts (planned: in the next seven days); `free` is the next day with free
 * times ({ date, dayLabel, count }) or null.
 */
export function thoughtsFor({ path = '', now = new Date(), can = {}, brand = {}, drafts = 0, planned = 0, free = null } = {}) {
  const out = []
  const diary = /^\/diary/.test(path)
  const marketing = /^\/marketing/.test(path)
  const social = /^\/marketing-social/.test(path)
  const finance = /^\/(transactions|finance)/.test(path)
  const m = moment(now)
  const weekday = m.isoWeekday() // 1 Monday .. 7 Sunday
  const hour = m.hour()
  const week = m.format('GGGG-[W]WW')

  // Free times in the diary, made into a post
  if (can.social && free && free.count > 0 && free.dayLabel && (diary || marketing)) {
    const n = free.count
    out.push({
      id: 'free-times',
      key: `${free.date}:${n}`,
      score: 90,
      icon: 'calendar',
      said: `${free.dayLabel} has ${n} free ${plural(n, 'time', 'times')}. I could make ${plural(n, 'it', 'them')} into a post.`,
      label: `Post ${possessive(free.dayLabel)} free times`,
      cta: 'Make the post',
      act: { ask: `Post ${possessive(free.dayLabel)} free times` },
    })
  }

  // The brand kit, which every post and email wears
  if (can.brandKit && marketing) {
    if (!brand.set) {
      out.push({
        id: 'brand-kit',
        key: 'unset',
        score: 70,
        icon: 'palette',
        said: 'Your posts and emails use a guess at your colours. Shall we set up your brand kit?',
        label: 'Set up my brand kit',
        cta: 'Set it up',
        act: { ask: 'Help me set up my brand kit' },
      })
    } else if (!brand.logo) {
      out.push({
        id: 'brand-logo',
        key: 'no-logo',
        score: 35,
        icon: 'palette',
        said: 'Your brand kit has no logo yet. Want to add it?',
        label: 'Add my logo',
        cta: 'Add it',
        act: { go: 'brandKit' },
      })
    }
  }

  // Monday morning: the week's posts
  if (can.social && weekday === 1 && hour >= 6 && hour < 14 && planned < 3 && (diary || marketing)) {
    out.push({
      id: 'week-posts',
      key: week,
      score: 60,
      icon: 'sparkle',
      said: 'It’s Monday. Want this week’s posts planned?',
      label: 'Plan this week’s posts',
      cta: 'Plan them',
      act: { ask: 'Plan a week of posts for me' },
    })
  }

  // Posts waiting in drafts (Social opens on them)
  if (can.social && drafts >= 2 && !social && (diary || marketing)) {
    out.push({
      id: 'drafts',
      key: String(drafts),
      score: 50,
      icon: 'photo',
      said: `${drafts} posts are waiting in your drafts. Plan them for this week?`,
      label: 'Open my draft posts',
      cta: 'Open drafts',
      act: { path: '/marketing-social' },
    })
  }

  // The end of the week, and the start of a month: how it went
  if (can.finance && ((weekday === 5 && hour >= 15) || weekday === 6) && (diary || finance || marketing)) {
    out.push({
      id: 'week-review',
      key: week,
      score: 55,
      icon: 'chart',
      said: 'Nearly the weekend. Want to see how the week went?',
      label: 'How did this week go?',
      cta: 'Show me',
      act: { ask: 'How did this week go?' },
    })
  }
  if (can.finance && m.date() <= 3 && (diary || finance || marketing)) {
    const last = moment(now).subtract(1, 'month').format('MMMM')
    out.push({
      id: 'month-review',
      key: m.format('YYYY-MM'),
      score: 58,
      icon: 'chart',
      said: `A new month. Want to see how ${last} went?`,
      label: `How was ${last}?`,
      cta: 'Show me',
      act: { ask: `How was ${last}?` },
    })
  }

  return out.sort((a, b) => b.score - a.score)
}

/**
 * The ask box's idea rows: this page's best thoughts in the person's words,
 * then the page's own questions, three at most and no repeats. `asks` are
 * the page's questions (knowledge.js), `fallback` a last "What can you do
 * here?".
 */
export function ideaRows(thoughts = [], asks = [], fallback = null, memory = null, now = Date.now()) {
  const seen = new Set()
  const rows = []
  const add = (row) => {
    const k = String(row.label || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    if (!k || seen.has(k) || rows.length >= 3) return
    seen.add(k)
    rows.push(row)
  }
  thoughts.filter(t => !isDone(memory, t, now)).forEach(t => add({ id: t.id, label: t.label, act: t.act, icon: t.icon }))
  asks.forEach(q => add({ id: `ask:${q}`, label: q, act: { ask: q }, icon: 'chat' }))
  if (fallback) add({ id: 'ask:fallback', label: fallback, act: { ask: fallback }, icon: 'chat' })
  return rows
}

// ─── Memory ───
// { sessions: [at], lastAt, lastPath, streak, quietUntil, snoozed: { id: until },
//   done: { id: { key, at } }, shown: { id: { day, count } } }
export const blankMemory = () => ({ sessions: [], lastAt: 0, lastPath: '', streak: 0, quietUntil: 0, snoozed: {}, done: {}, shown: {} })

const dayOf = (at) => moment(at).format('YYYY-MM-DD')
const isDone = (memory, t, now) => {
  const d = memory && memory.done && memory.done[t.id]
  return !!d && d.key === t.key && now - d.at < RHYTHM.doneDays * DAY
}

/**
 * Whether a session may start: not while Bailey's keeping quiet, not before
 * the page has had a quiet moment, not too often, and not again on the same
 * page for a while.
 */
export function canStart(memory, { now = Date.now(), path = '', pageSince = now } = {}) {
  const mem = memory || blankMemory()
  if (now < (mem.quietUntil || 0)) return false
  if (now - pageSince < RHYTHM.firstAfter) return false
  const hour = (mem.sessions || []).filter(at => now - at < 60 * 60 * 1000)
  if (hour.length >= RHYTHM.perHour) return false
  if (!mem.lastAt) return true
  const since = now - mem.lastAt
  return (since >= RHYTHM.gap && path !== mem.lastPath) || since >= RHYTHM.samePageGap
}

/** Up to three thoughts for a session, leaving out closed, done and worn-out ones. */
export function pickSession(thoughts, memory, now = Date.now()) {
  const mem = memory || blankMemory()
  const today = dayOf(now)
  return (thoughts || [])
    .filter(t => !((mem.snoozed || {})[t.id] > now))
    .filter(t => !isDone(mem, t, now))
    .filter((t) => {
      const s = (mem.shown || {})[t.id]
      return !(s && s.day === today && s.count >= RHYTHM.shownPerDay)
    })
    .slice(0, RHYTHM.perSession)
}

// Each of these returns the memory as it should now be kept.
export function noteSession(memory, { now = Date.now(), path = '' } = {}) {
  const mem = { ...blankMemory(), ...memory }
  return { ...mem, lastAt: now, lastPath: path, sessions: [...(mem.sessions || []).filter(at => now - at < 60 * 60 * 1000), now] }
}
export function noteShown(memory, id, now = Date.now()) {
  const mem = { ...blankMemory(), ...memory }
  const today = dayOf(now)
  const was = mem.shown[id]
  return { ...mem, shown: { ...mem.shown, [id]: { day: today, count: was && was.day === today ? was.count + 1 : 1 } } }
}
export function noteDone(memory, thought, now = Date.now()) {
  const mem = { ...blankMemory(), ...memory }
  return { ...mem, streak: 0, done: { ...mem.done, [thought.id]: { key: thought.key, at: now } } }
}
/**
 * Closed: that idea sleeps for a few days. The third close in a row keeps
 * Bailey quiet until tomorrow; `quiet` says so, for his one line about it.
 */
export function noteClosed(memory, thought, now = Date.now()) {
  const mem = { ...blankMemory(), ...memory }
  const snoozed = { ...mem.snoozed, [thought.id]: now + RHYTHM.snoozeDays * DAY }
  const streak = (mem.streak || 0) + 1
  if (streak >= RHYTHM.quietAfter) {
    return { memory: { ...mem, snoozed, streak: 0, quietUntil: moment(now).add(1, 'day').startOf('day').valueOf() }, quiet: true }
  }
  return { memory: { ...mem, snoozed, streak }, quiet: false }
}

// Kept per person on this device
const KEY = (userId) => `beu-bailey-thoughts-${userId || 'me'}`
export const loadMemory = (userId) => {
  try { return { ...blankMemory(), ...(JSON.parse(localStorage.getItem(KEY(userId))) || {}) } } catch (e) { return blankMemory() }
}
export const saveMemory = (userId, memory) => {
  // Old entries go, so the memory stays small
  const now = Date.now()
  const snoozed = {}
  Object.keys(memory.snoozed || {}).forEach((id) => { if (memory.snoozed[id] > now) snoozed[id] = memory.snoozed[id] })
  const done = {}
  Object.keys(memory.done || {}).forEach((id) => { if (now - memory.done[id].at < RHYTHM.doneDays * DAY) done[id] = memory.done[id] })
  const kept = { ...memory, snoozed, done }
  try { localStorage.setItem(KEY(userId), JSON.stringify(kept)) } catch (e) { /* private mode: he forgets, which is fine */ }
  return kept
}
